import React, { useState, useRef, useEffect } from "react";
import { Region_comunas } from "../../utils/Region_comunas";

const ComunaTreeSelect = ({ value, onChange, className }) => {
    const [open, setOpen] = useState(false);
    const [expandedRegions, setExpandedRegions] = useState({});
    const [search, setSearch] = useState("");
    const containerRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Auto-expand regions that match the search
    useEffect(() => {
        if (search.trim() === "") {
            setExpandedRegions({});
            return;
        }
        const lower = search.toLowerCase();
        const newExpanded = {};
        Object.entries(Region_comunas).forEach(([region, comunas]) => {
            const regionMatch = region.toLowerCase().includes(lower);
            const comunaMatch = comunas.some((c) => c.toLowerCase().includes(lower));
            if (regionMatch || comunaMatch) {
                newExpanded[region] = true;
            }
        });
        setExpandedRegions(newExpanded);
    }, [search]);

    const toggleRegion = (region) => {
        setExpandedRegions((prev) => ({ ...prev, [region]: !prev[region] }));
    };

    const handleSelectComuna = (comuna) => {
        onChange(comuna);
        setOpen(false);
        setSearch("");
    };

    const handleClear = (e) => {
        e.stopPropagation();
        onChange("");
    };

    const lower = search.toLowerCase();

    const filteredRegions = Object.entries(Region_comunas)
        .map(([region, comunas]) => {
            const regionMatch = region.toLowerCase().includes(lower);
            const filteredComunas = search.trim()
                ? comunas.filter((c) => c.toLowerCase().includes(lower) || regionMatch)
                : comunas;
            return { region, comunas: filteredComunas };
        })
        .filter(({ comunas }) => comunas.length > 0);

    return (
        <div
            ref={containerRef}
            style={{ position: "relative", width: "100%" }}
        >
            {/* Trigger box (closed) */}
            {!open && (
                <div
                    onClick={() => { setOpen(true); setSearch(""); }}
                    className={className}
                    style={{
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        userSelect: "none",
                        minHeight: "32px",
                        boxSizing: "border-box",
                    }}
                >
                    <span style={{ flexGrow: 1, color: value ? "inherit" : "#000000ff", textAlign: "left", paddingLeft: "4px" }}>
                        {value || "- Seleccione -"}
                    </span>
                    <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        {value && (
                            <span
                                onClick={handleClear}
                                style={{
                                    cursor: "pointer",
                                    color: "#999",
                                    fontSize: "14px",
                                    lineHeight: 1,
                                    padding: "0 2px",
                                }}
                                title="Limpiar"
                            >
                                ✕
                            </span>
                        )}
                        <span style={{
                            width: "12px",
                            height: "12px",
                            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath fill='%23767676' d='M4 6l4 4 4-4z'/%3E%3C/svg%3E")`,
                            backgroundRepeat: "no-repeat",
                            backgroundPosition: "center",
                            backgroundSize: "16px 16px",
                            display: "inline-block",
                            flexShrink: 0,
                        }} />
                    </span>
                </div>
            )}

            {/* Search input replacing trigger when open */}
            {open && (
                <input
                    autoFocus
                    type="text"
                    placeholder="Buscar región o comuna..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className={className}
                    style={{
                        width: "100%",
                        boxSizing: "border-box",
                        outline: "none",
                        paddingLeft: "6px",
                    }}
                />
            )}

            {/* Dropdown tree list */}
            {open && (
                <div
                    style={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        zIndex: 9999,
                        background: "#fff",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                        width: "100%",
                        minWidth: "180px",
                        maxHeight: "280px",
                        overflowY: "auto",
                    }}
                >
                    {filteredRegions.length === 0 && (
                        <div style={{ padding: "10px", fontSize: "13px", color: "#888", textAlign: "center" }}>
                            Sin resultados
                        </div>
                    )}
                    {filteredRegions.map(({ region, comunas }) => (
                        <div key={region}>
                            {/* Region header (not selectable) */}
                            <div
                                onClick={() => toggleRegion(region)}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    padding: "6px 10px",
                                    cursor: "pointer",
                                    fontWeight: "600",
                                    fontSize: "12px",
                                    backgroundColor: "#ffffff",
                                    userSelect: "none",
                                    borderBottom: "1px solid #eee",
                                    gap: "6px",
                                }}
                            >
                                <span style={{ fontSize: "10px" }}>
                                    {expandedRegions[region] ? "▼" : "▶"}
                                </span>
                                {region}
                            </div>

                            {/* Comunas */}
                            {(expandedRegions[region] || search.trim() !== "") && (
                                <div>
                                    {comunas.map((comuna) => (
                                        <div
                                            key={comuna}
                                            onClick={() => handleSelectComuna(comuna)}
                                            style={{
                                                padding: "6px 10px 6px 26px",
                                                cursor: "pointer",
                                                textAlign: "left",
                                                color: value === comuna ? "#1976d2" : "#333",
                                                backgroundColor: value === comuna ? "#e3f2fd" : "transparent",
                                                transition: "background 0.15s",
                                            }}
                                            onMouseEnter={(e) => {
                                                if (value !== comuna)
                                                    e.currentTarget.style.backgroundColor = "#f0f0f0";
                                            }}
                                            onMouseLeave={(e) => {
                                                if (value !== comuna)
                                                    e.currentTarget.style.backgroundColor = "transparent";
                                            }}
                                        >
                                            {comuna}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ComunaTreeSelect;
